import { useEffect, useState } from 'react';

import { Transfer, Position, BalanceSummaryView } from 'src/models';

import { useBalanceSelector } from './useBalanceSelector';
import { useCumulativeOrderListSelector } from './useCumulativeOrderListSelector';
import { usePositionListSelector } from './usePositionListSelector';
import { useTransferListSelector } from './useTransferListSelector';

interface UseBalanceSummaryReturnType extends BalanceSummaryView {}

export const useBalanceSummary = (): UseBalanceSummaryReturnType => {
  const [pendingTransfersTotal, setPendingTransfersTotal] = useState<number>(0);
  const [nonTradedPositionsTotal, setNonTradedPositionsTotal] = useState<number>(0);
  const [tradedPositionsTotal, setTradedPositionsTotal] = useState<number>(0);
  const [workingOrdersTotal, setWorkingOrdersTotal] = useState<number>(0);

  const { balance, hasBalanceError, balanceError, isBalanceLoading } = useBalanceSelector();
  const {
    tradablePositionList,
    nonTradablePositionList,
    isPositionListLoading,
    hasPositionListError,
    positionListError,
  } = usePositionListSelector();

  const { cumulativeOrderList, hasCumulativeOrderListError, cumulativeOrderListError, isCumulativeOrderListLoading } =
    useCumulativeOrderListSelector();
  const { transferList, isTransferListLoading, hasTransferListError, transferListError } = useTransferListSelector();

  const findPendingTransfersTotal = () => {
    return transferList
      .filter((transfer: Transfer) => transfer.status.isProcessing || transfer.status.isPending)
      .reduce((total, transfer) => total + (transfer.direction.isDeposit ? transfer.amount : -transfer.amount), 0);
  };

  const findTradedPositionsTotal = () => {
    return tradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const findNonTradedPositionsTotal = () => {
    return nonTradablePositionList.reduce((acc: number, object: Position) => acc + (object?.totalValue || 0), 0) || 0;
  };

  const findWorkingOrdersTotal = () => {
    return cumulativeOrderList.reduce((total: number, order) => {
      if (order.action.isBuy) {
        const orderCost = order.isPartial ? -((order.quantity - order.filledQuantity) * order.price) : order.totalCost;

        return total + orderCost;
      }

      return total;
    }, 0);
  };

  useEffect(() => {
    setPendingTransfersTotal(findPendingTransfersTotal());
  }, [transferList]);

  useEffect(() => {
    setTradedPositionsTotal(findTradedPositionsTotal());
  }, [tradablePositionList]);

  useEffect(() => {
    setNonTradedPositionsTotal(findNonTradedPositionsTotal());
  }, [nonTradablePositionList]);

  useEffect(() => {
    setWorkingOrdersTotal(findWorkingOrdersTotal());
  }, [cumulativeOrderList]);

  return {
    cashAvailable: {
      value: balance.cashAvailable,
      hasError: hasBalanceError,
      error: balanceError,
      isLoading: isBalanceLoading,
    },
    cashBalance: {
      value: balance.cashBalance,
      hasError: hasBalanceError,
      error: balanceError,
      isLoading: isBalanceLoading,
    },
    accountValue: {
      value: balance.cashBalance + tradedPositionsTotal + nonTradedPositionsTotal,
      hasError: hasBalanceError || hasPositionListError,
      error: balanceError || positionListError,
      isLoading: isBalanceLoading || isPositionListLoading,
    },
    pendingTransfersTotal: {
      value: pendingTransfersTotal,
      hasError: hasTransferListError,
      error: transferListError,
      isLoading: isTransferListLoading,
    },
    tradedPositionsTotal: {
      value: tradedPositionsTotal,
      hasError: hasPositionListError,
      error: positionListError,
      isLoading: isPositionListLoading,
    },
    nonTradedPositionsTotal: {
      value: nonTradedPositionsTotal,
      hasError: hasPositionListError,
      error: positionListError,
      isLoading: isPositionListLoading,
    },
    workingOrdersTotal: {
      value: workingOrdersTotal,
      hasError: hasCumulativeOrderListError,
      error: cumulativeOrderListError,
      isLoading: isCumulativeOrderListLoading,
    },
    pendingOfferOrdersTotal: {
      value: balance.pendingOfferOrdersTotal,
      hasError: hasBalanceError,
      error: balanceError,
      isLoading: isBalanceLoading,
    },
  };
};
